/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  AfterViewInit,
  Injector,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";

//services
import { ClientPromiseLookupService, UserPromiseLookupService } from "shared/cubiq-services";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../../../golden-layout/golden-layout-service";
import { DashboardItemType } from "../../../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../../golden-layout/golden-layout-addition-mode";
import { ClientImpl, ClientContactImpl, UserImpl } from "shared/cubiq-model-multi-asset";
import { ICAutocompleteGenericModel } from "./models/bfx-interface-controls.models";

export const CONTACTS_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxContactsInputComponent),
  multi: true,
};

export const CONTACTS_LABEL_SORT: any = (a, b) => {
  return a.contactNameTxt.toLowerCase() < b.contactNameTxt.toLowerCase()
    ? -1
    : a.contactNameTxt.toLowerCase() > b.contactNameTxt.toLowerCase()
      ? 1
      : 0;
};
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICNInputControlState } from "./models/bfx-interface-controls.enums";

import { BfxTextInputComponent } from "../../../../shared/base-components/bfx-text-input";
import { SizeMixin } from "shared/base-components/mixins/size";
import { EmphasisMixin } from "shared/base-components/mixins/emphasis";
import { InputLabelStateMixin } from "shared/base-components/mixins/label-state";
import { DisabledMixin } from "shared/base-components/mixins/disabled";
import { ComponentMixin } from "shared/base-components/mixins/mixin";
import { BfxInputDirective } from "shared/base-components/bfx-input";
const Mixins = SizeMixin(EmphasisMixin(InputLabelStateMixin(DisabledMixin(ComponentMixin))));

@Component({
  selector: "bfx-contacts-input",
  templateUrl: "./html/bfx-contacts-input.html",
  providers: [CONTACTS_VALUE_ACCESSOR],
  host: {
    "[class.interface-control__contacts-input__container]": "true",
    "[class.fieldsGroup]": "fieldsGroup",
  },
  standalone: false,
})
export class BfxContactsInputComponent
  extends Mixins
  implements AfterViewInit, ControlValueAccessor, OnDestroy
{
  /***************************************/
  /* INPUT / OUTPUT */

  @Input() inputId: string;

  private _client: ClientImpl;

  public get client(): ClientImpl {
    return this._client;
  }

  @Input()
  public set client(value: ClientImpl) {
    const changed = this._client != value;

    this._client = value;

    if (changed) {
      this.validateClientSelection();
    }
  }

  @Input("label") input_label: string;

  @Input() selectedContacts: Array<ClientContactImpl> = [];

  @Input() selectedTrader: UserImpl | ICAutocompleteGenericModel = null;

  @Output() contactChanged: EventEmitter<Array<CubiqTS.ClientContactImpl>> = new EventEmitter<
    Array<CubiqTS.ClientContactImpl>
  >();

  @Output() commentsChanged: EventEmitter<{
    privateComment: string;
    clientComment: string;
    clientTraderComment?: string;
    hasClientTraderComment?: boolean;
  }> = new EventEmitter<{ privateComment: string; clientComment: string }>();

  @Output() traderChanged: EventEmitter<UserImpl> = new EventEmitter<UserImpl>();

  @Output() contactManagerOpen: EventEmitter<void> = new EventEmitter<void>();

  @Output() contactManagerClosed: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild("inputField", { static: true }) inputField: BfxTextInputComponent;

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  public textValue: string = "";

  /* External Validation */
  input_externalState: ICNInputControlState;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  @Input()
  fieldsGroup: boolean = false;

  /***************************************/
  /* DATA */
  public config: any = {};

  public data: any = {
    label: null,
    value: null,
  };

  /** Do not output changes on loading */
  private isLoading: boolean = true;
  /** Should default clients be loaded when client is changed? Only set when not loading */
  private applyClientDefaults: boolean = false;

  public locked: boolean = false;
  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    injector: Injector,
    public element: ElementRef,
    public layoutService: GoldenLayoutService,
    public clientService: ClientPromiseLookupService,
    public userService: UserPromiseLookupService,
    public translationService: TranslateService,
    public cd: ChangeDetectorRef,
    public ngZone: NgZone,
    public inputDirective: BfxInputDirective,
  ) {
    super(injector);
    this.size = "sm";
  }

  ngAfterViewInit(): void {
    this.initialize();
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* INITIALIZATION */
  protected initialize(): void {
    this.data.label =
      this.input_label != null && this.input_label != "" ? this.input_label : "Contacts";
  }

  /**
   * Called when all fields are correctly set to prevent mutations of caller during asynchronous loading
   */
  public loadComplete() {
    this.isLoading = false;
    this.validateClientSelection(true).then(() => (this.applyClientDefaults = true));
  }

  /**********************************************************************/
  /* HANDLERS */

  public focusInput() {
    this.inputField.focus();
  }

  public onFocus(event: any): void {
    this.onModelTouched();
  }

  public onBlur(event: any): void {
    this.onChange(this.data.value);
  }

  public onChange(event: any): void {
    if (!this.isLoading) {
      this.onModelChange(this.data.value);
    }
  }

  writeClient(client: any): void {
    this._client = client;

    this.inputField.setDisabledState(this.disabled || client == null || this.locked);

    this.cd.markForCheck();
  }

  writeClientTrader(clientTrader: any): void {
    this.selectedTrader = clientTrader;
    this.cd.markForCheck();
  }

  writeValue(value: any): void {
    if (Array.isArray(value)) {
      this.selectedContacts = value;
      this.data.value = value
        .sort(CONTACTS_LABEL_SORT)
        .map((clientContact: ClientContactImpl) => clientContact.contactNameTxt)
        .join(", ");
    } else {
      this.data.value = value;
    }

    this.inputField.writeValue(this.data.value);
    this.cd.markForCheck();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* METHODS */
  public resetInputControl(): void {
    this.data.label =
      this.input_label != null && this.input_label != "" ? this.input_label : "Contacts";
    this.data.value = null;
    this.onChange(null);
  }

  public lockInputControl(): void {
    this.setLocked(true);
  }

  public unlockInputControl(): void {
    this.setLocked(false);
  }

  private setLocked(value: boolean) {
    this.locked = value;
    this.disabled = value;
    this.inputLabelState = value ? "disabled" : "enabled";
  }

  public onClick(event: Event) {
    if (!this.locked && this.client != null) {
      this.openContactManager(event);
    }
  }

  public isReady() {
    return this.selectedContacts != null && this.selectedContacts.length > 0;
  }

  public openContactManager(event: Event) {
    if (!this.isLoading) {
      let config: GLPopoutConfiguration = new GLPopoutConfiguration();

      config.initialState = GLPopoutState.POPOUT;
      config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
      config.popoutResizable = false;
      config.preferredSize = { width: 720, height: 440 };
      config.sourceEvent = event;

      config.data = {
        client: this.client,
        selectedContacts: this.selectedContacts,
        selectedTrader: this.selectedTrader,
        contactCallback: this.onContactsChanged.bind(this),
        commentCallback: this.onCommentChanged.bind(this),
        traderCallback: this.onTraderChanged.bind(this),
        closeCallback: this.onContactManagerClosed.bind(this),
      };

      let title: string = "Contacts Manager";

      this.contactManagerOpen.emit();
      this.layoutService.launchFromDashboard(
        title,
        DashboardItemType.ContactManager,
        true,
        GoldenLayoutAdditionMode.DEFAULT,
        config,
      );
    }
  }

  public onContactsChanged(selectedContacts: ClientContactImpl[]): void {
    if (!this.isLoading) {
      let sortedContacts = [...selectedContacts].sort(CONTACTS_LABEL_SORT);
      this.data.value = sortedContacts
        .map((clientContact: ClientContactImpl) => clientContact.contactNameTxt)
        .join(", ");
      this.selectedContacts = selectedContacts;
      this.contactChanged.emit(selectedContacts);
      this.onChange(null);
      this.cd.markForCheck();
    }
  }

  public onCommentChanged(
    privateComment: string,
    clientComment: string,
    clientTrader?: UserImpl,
  ): void {
    if (!this.isLoading) {
      if (this.client != null) {
        this.client.defaultClientComment = clientComment;
        this.client.defaultPrivateComment = privateComment;
      }

      if (clientTrader && clientTrader.comment) {
        this.commentsChanged.emit({
          privateComment: privateComment,
          clientComment: clientComment,
          clientTraderComment: ` ${clientTrader.userName}: ${clientTrader.comment}`, // ${clientTrader.userName}:
          hasClientTraderComment: true,
        });
      } else {
        this.commentsChanged.emit({
          privateComment: privateComment,
          clientComment: clientComment,
        });
      }
      this.cd.markForCheck();
    }
  }

  public onTraderChanged(trader: UserImpl): void {
    if (!this.isLoading) {
      if (trader != null && trader instanceof UserImpl) {
        // contacts manager directly amends user object
        // make sure selectedTrader has value set
        trader["selected"] = true;
      }

      this.selectedTrader = trader;
      this.traderChanged.emit(trader);
      this.cd.markForCheck();
    }
  }

  public onContactManagerClosed(): void {
    if (!this.isLoading) {
      this.contactManagerClosed.emit();
      this.cd.markForCheck();
    }
  }

  /**
   * Validate all values and return that promise that resolves once async fetch operations are completed and verified
   *
   */
  validateClientSelection(postLoad: boolean = false): Promise<void[]> {
    if (this.client == null && this.data.prevClientId != null) {
      this.data.prevClientId = null;

      // ask binding holder to update backing value of selectedContacts
      this.onContactsChanged([]);
      // ask binding holder to update backing value of selectedTrader
      this.onTraderChanged(null);
    }
    if (
      this.client != null &&
      (this.data.prevClientId == null || this.client.clientId != this.data.prevClientId)
    ) {
      this.data.prevClientId = this.client.clientId;

      if (!this.isLoading && postLoad != true) {
        // Client was changed post loading, clear contacts selection for
        // validateContactSelection to populate the defaults
        this.selectedContacts = null;
      }

      return Promise.all([
        this.validateContactSelection(this.client),
        this.validateClientTraderSelection(),
      ]);
    }
    return Promise.resolve([]);
  }

  /**
   *
   * @param client
   * @returns Promise that resolves only after asynchronously validating contacts
   */
  private validateContactSelection(client): Promise<void> {
    return this.clientService
      .getActiveClientContacts(client.clientId)
      .then((contacts: CubiqTS.ClientContactImpl[]) => {
        if (contacts == null) {
          // no contacts available
          this.onContactsChanged([]);
          return;
        }

        if (Array.isArray(contacts) && Array.isArray(this.selectedContacts)) {
          let ids = new Set(
            contacts.map((c) => {
              return c.id.toString();
            }),
          );

          let valid = this.selectedContacts
            .map((c: CubiqTS.ClientContactImpl) => {
              return ids.has(c.id.toString());
            })
            .reduce((p, c) => {
              return p && c;
            }, true);

          if (valid) {
            // valid existing selection so reselect + notify listeners
            this.onContactsChanged(this.selectedContacts);
            return;
          }
        }

        // no valid existing selection fallback to default selection
        let defaults: CubiqTS.ClientContactImpl[] = contacts.filter(
          (contact: CubiqTS.ClientContactImpl) => contact.isDefaultFg,
        );
        this.onContactsChanged(defaults);
        this.cd.markForCheck();
      });
  }

  /**
   * @returns Promise that resolves only after asynchronously validating client trader users
   */
  private validateClientTraderSelection(): Promise<void> {
    return this.userService.isPrimed().then(() => {
      this.userService.getUsersByClientCode(this.client.clientId).then((users: UserImpl[]) => {
        if (!Array.isArray(users)) {
          this.onTraderChanged(null);
          return;
        }

        let selectedTraderCode = null;

        if (this.selectedTrader != null && this.selectedTrader instanceof CubiqTS.UserImpl) {
          selectedTraderCode = this.selectedTrader.userCode;
        } else if (
          this.selectedTrader != null &&
          this.selectedTrader instanceof ICAutocompleteGenericModel
        ) {
          selectedTraderCode = this.selectedTrader.value?.userCode;
        }

        if (selectedTraderCode != null) {
          selectedTraderCode =
            selectedTraderCode.userCode != null ? selectedTraderCode.userCode : selectedTraderCode;

          let clientTraders: CubiqTS.UserImpl[] = users.filter(
            (user: UserImpl) =>
              user.userType == CubiqTS.UserType.CLIENT_TRADER &&
              user.userCode == selectedTraderCode,
          );

          if (clientTraders.length == 1) {
            // reselect existing and notify
            this.onTraderChanged(clientTraders[0]);
            return;
          }
        }

        if (this.applyClientDefaults) {
          // Only want to set the defaultClientTrader when flag is set - not when loading which may
          // have different set (or no) client traders
          let defaults: CubiqTS.UserImpl[] = users.filter(
            (user: UserImpl) => user.userType == CubiqTS.UserType.CLIENT_TRADER && user.isDefault,
          );

          if (defaults.length == 1) {
            this.onTraderChanged(defaults[0]);
          } else {
            this.onTraderChanged(null);
          }
        }
      });
    });
  }
}
